/**
 * LoyaltyRedemption — issue #35
 *
 * Point-redemption widget mounted from the Payments page (and reusable
 * from the future PaymentModal). Computes the dollar value of a
 * point-redemption against the active loyalty config (default 10:1 —
 * 100 points = $10, 250 = $25, 500 = $50 per HTHD's tier table) and
 * exposes the resulting cents discount to the parent so the parent
 * can subtract it from the order total before charging the remainder
 * via the selected payment method.
 *
 * Acceptance criteria coverage (#35):
 *
 *  1. Sufficient points → redemption applies, points reduce. Done via
 *     `onRedemptionApplied(redemptionId, valueCents, pointsToRedeem)`
 *     callback after a successful POST to /api/admin/redemptions/create.
 *     The parent subtracts `valueCents` from total and refreshes the
 *     customer's points_balance from the response.
 *
 *  2. Insufficient points → clear "need X more points" message. Rendered
 *     inline when the staff selects a tier the customer can't afford.
 *
 *  3. Split payment → parent reads `appliedValueCents` and charges the
 *     remainder via Card on File / Tap-to-Pay / Manual. The component
 *     itself does NOT settle the remainder — separation of concerns
 *     keeps the loyalty UI agnostic of the payment-method picker.
 *
 *  4. Redemption row in customer's history → server's
 *     adminRedemptionsApi.create writes the Redemption row tagged with
 *     the booking link. The customer's account view reads it back via
 *     the existing PointsTransaction + Redemption history endpoint.
 *
 * Notes on intent (load-bearing for #29 + #35):
 *
 *  - The redemption is "applied" to this checkout in-memory until the
 *    parent fires the actual settle call. We expose a `clear()` handler
 *    so the parent can roll back the redemption locally if the staff
 *    cancels — the server-side Redemption row only writes on the
 *    explicit redeem button (not on tier selection).
 *
 *  - We deliberately mirror Loyalty.tsx's 3-tier scheme (100/250/500
 *    points → $10/$25/$50) so HTHD's owner sees the same redemption
 *    options on both Loyalty and Payments pages. If the loyalty config
 *    changes to a different tier table, both surfaces should re-read
 *    from a shared client config — out of scope for #35.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  adminRedemptionsApi,
  type CreateRedemptionResponse,
} from '../../lib/api.js';

export type RedemptionTier = {
  readonly points: number;
  readonly discountCents: number;
};

export const DEFAULT_REDEMPTION_TIERS: ReadonlyArray<RedemptionTier> = [
  { points: 100, discountCents: 1000 },
  { points: 250, discountCents: 2500 },
  { points: 500, discountCents: 5000 },
];

export type AppliedRedemption = {
  readonly redemptionId: string;
  readonly redemptionCode: string;
  readonly pointsRedeemed: number;
  readonly valueCents: number;
  readonly customerId: string;
  /** New customer points balance after the deduction (from server). */
  readonly newBalance: number;
};

type LoyaltyRedemptionProps = {
  /** API base URL — typically `config.deployment.apiUrl`. */
  readonly apiUrl: string;
  /** Customer to redeem against. Required — staff must select a customer first. */
  readonly customer: {
    readonly id: string;
    readonly name: string;
    readonly pointsBalance: number;
  } | null;
  /** Tiers to render. Defaults to HTHD's 100/250/500 scheme. */
  readonly tiers?: ReadonlyArray<RedemptionTier>;
  /** Current applied redemption (parent owns the lifecycle for split-payment). */
  readonly applied: AppliedRedemption | null;
  /** Called when a redemption is successfully created server-side. */
  readonly onRedemptionApplied: (applied: AppliedRedemption) => void;
  /** Called when the staff clears a previously-applied redemption. */
  readonly onRedemptionCleared: () => void;
  /**
   * Current checkout balance in cents — used to show how much the tier
   * covers vs leaves remaining. Optional; when null, the split-payment
   * preview is hidden and the tier list is the only UI.
   */
  readonly checkoutTotalCents?: number;
};

function formatPoints(n: number): string {
  return n.toLocaleString('en-US');
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function LoyaltyRedemption({
  apiUrl,
  customer,
  tiers = DEFAULT_REDEMPTION_TIERS,
  applied,
  onRedemptionApplied,
  onRedemptionCleared,
  checkoutTotalCents,
}: LoyaltyRedemptionProps): JSX.Element {
  const [selectedTier, setSelectedTier] = useState<RedemptionTier | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortedTiers = useMemo(
    () => [...tiers].sort((a, b) => a.points - b.points),
    [tiers],
  );

  const handleSelectTier = useCallback(
    (tier: RedemptionTier) => {
      if (customer && customer.pointsBalance < tier.points) {
        setSelectedTier(tier);
        setError(
          `Customer needs ${formatPoints(
            tier.points - customer.pointsBalance,
          )} more points to redeem this tier.`,
        );
        return;
      }
      setSelectedTier(tier);
      setError(null);
    },
    [customer],
  );

  const handleRequestConfirm = useCallback(() => {
    if (!customer || !selectedTier) return;
    if (customer.pointsBalance < selectedTier.points) {
      setError(
        `Customer needs ${formatPoints(selectedTier.points - customer.pointsBalance)} more points to redeem this tier.`,
      );
      return;
    }
    setPendingConfirm(true);
    setError(null);
  }, [customer, selectedTier]);

  const handleCancelConfirm = useCallback(() => {
    setPendingConfirm(false);
  }, []);

  const handleApplyRedemption = useCallback(async () => {
    if (!customer || !selectedTier) return;
    if (customer.pointsBalance < selectedTier.points) {
      setError(
        `Customer needs ${formatPoints(selectedTier.points - customer.pointsBalance)} more points to redeem this tier.`,
      );
      setPendingConfirm(false);
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await adminRedemptionsApi.create(apiUrl, {
      customer_id: customer.id,
      reward_tier: String(selectedTier.points) as '100' | '250' | '500',
    });
    setSubmitting(false);
    setPendingConfirm(false);
    if (result.error || !result.data) {
      setError(result.error ?? 'Could not apply redemption');
      return;
    }
    const r: CreateRedemptionResponse = result.data;
    onRedemptionApplied({
      redemptionId: r.redemption.id,
      redemptionCode: r.redemption.redemption_code,
      pointsRedeemed: selectedTier.points,
      // Server's `discount_value` is a dollar number, not cents.
      valueCents: Math.round(r.discount_to_apply * 100),
      customerId: customer.id,
      newBalance: r.customer.new_balance,
    });
    setSelectedTier(null);
  }, [apiUrl, customer, selectedTier, onRedemptionApplied]);

  const handleClear = useCallback(() => {
    setSelectedTier(null);
    setError(null);
    setPendingConfirm(false);
    onRedemptionCleared();
  }, [onRedemptionCleared]);

  if (!customer) {
    return (
      <section
        className="ferro-card"
        aria-label="Loyalty redemption"
        data-testid="loyalty-redemption-empty"
      >
        <p className="ferro-muted">
          Select a customer to apply loyalty points.
        </p>
      </section>
    );
  }

  if (applied) {
    return (
      <section
        className="ferro-card"
        aria-label="Loyalty redemption (applied)"
        data-testid="loyalty-redemption-applied"
      >
        <header className="ferro-card__header">
          <h3 className="ferro-section__subtitle">Loyalty redemption</h3>
          <span className="ferro-badge ferro-badge--success">Applied</span>
        </header>
        <dl className="ferro-intake__statusGrid">
          <div>
            <dt>Redemption code</dt>
            <dd data-testid="loyalty-applied-code">
              <code>{applied.redemptionCode}</code>
            </dd>
          </div>
          <div>
            <dt>Points used</dt>
            <dd data-testid="loyalty-applied-points">
              {formatPoints(applied.pointsRedeemed)}
            </dd>
          </div>
          <div>
            <dt>Discount</dt>
            <dd data-testid="loyalty-applied-value">
              −{formatMoney(applied.valueCents)}
            </dd>
          </div>
          <div>
            <dt>New balance</dt>
            <dd data-testid="loyalty-applied-balance">
              {formatPoints(applied.newBalance)} pts
            </dd>
          </div>
        </dl>
        <footer className="ferro-card__footer">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={handleClear}
            data-testid="loyalty-clear-redemption"
          >
            Clear redemption
          </button>
        </footer>
      </section>
    );
  }

  // Confirmation step: clearly preview the redemption + split-payment
  // math before the staff commits. Pattern modeled on Becky's T-1.1
  // tier-confirmation prompt — show the customer the exact dollar
  // discount and remaining balance, give them an explicit confirm/cancel.
  if (pendingConfirm && selectedTier) {
    const previewRemaining =
      typeof checkoutTotalCents === 'number'
        ? Math.max(0, checkoutTotalCents - selectedTier.discountCents)
        : null;
    const coversFullTotal =
      typeof checkoutTotalCents === 'number' &&
      selectedTier.discountCents >= checkoutTotalCents;
    return (
      <section
        className="ferro-card"
        aria-label="Confirm redemption"
        data-testid="loyalty-redemption-confirm"
      >
        <header className="ferro-card__header">
          <h3 className="ferro-section__subtitle">Confirm redemption</h3>
        </header>
        <dl className="ferro-intake__statusGrid">
          <div>
            <dt>Customer</dt>
            <dd data-testid="loyalty-confirm-customer">{customer.name}</dd>
          </div>
          <div>
            <dt>Tier</dt>
            <dd data-testid="loyalty-confirm-tier">
              {formatPoints(selectedTier.points)} pts →{' '}
              {formatMoney(selectedTier.discountCents)}
            </dd>
          </div>
          {typeof checkoutTotalCents === 'number' ? (
            <>
              <div>
                <dt>Checkout total</dt>
                <dd data-testid="loyalty-confirm-total">
                  {formatMoney(checkoutTotalCents)}
                </dd>
              </div>
              <div>
                <dt>Discount</dt>
                <dd data-testid="loyalty-confirm-discount">
                  −{formatMoney(selectedTier.discountCents)}
                </dd>
              </div>
              <div>
                <dt>Remaining to pay</dt>
                <dd data-testid="loyalty-confirm-remaining">
                  <strong>
                    {previewRemaining != null
                      ? formatMoney(previewRemaining)
                      : '—'}
                  </strong>
                  {coversFullTotal ? (
                    <span className="ferro-muted">
                      {' '}
                      (points cover the full total — no payment needed)
                    </span>
                  ) : null}
                </dd>
              </div>
            </>
          ) : null}
          <div>
            <dt>New balance</dt>
            <dd data-testid="loyalty-confirm-new-balance">
              {formatPoints(
                Math.max(0, customer.pointsBalance - selectedTier.points),
              )}{' '}
              pts
            </dd>
          </div>
        </dl>
        {error ? (
          <p className="ferro-form__error" role="alert">
            {error}
          </p>
        ) : null}
        <footer className="ferro-card__footer">
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => void handleApplyRedemption()}
            disabled={submitting}
            data-testid="loyalty-confirm-apply"
          >
            {submitting
              ? 'Applying…'
              : `Confirm redemption of ${formatPoints(selectedTier.points)} pts`}
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={handleCancelConfirm}
            disabled={submitting}
            data-testid="loyalty-confirm-cancel"
          >
            Cancel
          </button>
        </footer>
      </section>
    );
  }

  return (
    <section
      className="ferro-card"
      aria-label="Loyalty redemption"
      data-testid="loyalty-redemption"
    >
      <header className="ferro-card__header">
        <h3 className="ferro-section__subtitle">Loyalty redemption</h3>
        <span
          className="ferro-badge ferro-badge--neutral"
          data-testid="loyalty-balance"
        >
          {formatPoints(customer.pointsBalance)} pts available
        </span>
      </header>

      <p className="ferro-muted">
        Redeem {customer.name}'s points for an instant discount. The discount
        applies to this checkout — remaining balance is paid via the
        selected payment method.
      </p>

      <div
        className="ferro-form"
        role="group"
        aria-label="Redemption tiers"
        data-testid="loyalty-tier-grid"
      >
        {sortedTiers.map((tier) => {
          const canAfford = customer.pointsBalance >= tier.points;
          const isSelected = selectedTier?.points === tier.points;
          return (
            <button
              type="button"
              key={tier.points}
              className={`ferro-btn ${
                isSelected ? 'ferro-btn--primary' : 'ferro-btn--ghost'
              }`}
              onClick={() => handleSelectTier(tier)}
              disabled={!canAfford || submitting}
              data-testid={`loyalty-tier-${tier.points}`}
              aria-pressed={isSelected}
            >
              {formatPoints(tier.points)} pts → {formatMoney(tier.discountCents)}
              {!canAfford ? (
                <span className="ferro-muted" style={{ display: 'block', fontSize: '0.85em' }}>
                  Need {formatPoints(tier.points - customer.pointsBalance)} more
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {error ? (
        <p
          className="ferro-form__error"
          role="alert"
          data-testid="loyalty-error"
        >
          {error}
        </p>
      ) : null}

      <footer className="ferro-card__footer">
        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          onClick={handleRequestConfirm}
          disabled={
            !selectedTier ||
            submitting ||
            (!!selectedTier &&
              customer.pointsBalance < selectedTier.points)
          }
          data-testid="loyalty-apply-button"
        >
          {selectedTier
            ? `Redeem ${formatPoints(selectedTier.points)} pts`
            : 'Select a tier'}
        </button>
      </footer>
    </section>
  );
}
